import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";

// Module-scope formatter so we pay the (expensive) Intl construction cost
// once instead of on every cell render. The Loads grid alone calls this
// ~2000+ times per render (1000 rows × 2 date columns); per-call construction
// dominated the profile before this cache.
const NUMERIC_UTC_FORMATTER = new Intl.DateTimeFormat("en-US", {
	month: "2-digit",
	day: "2-digit",
	year: "numeric",
	timeZone: "UTC",
});

/**
 * Formats a date value as `MM/dd/yyyy` in UTC. Accepts `Date` instances,
 * ISO date strings, or nullish values. Returns an empty string for missing
 * values and "Invalid Date" for unparseable inputs. Dates with year <= 1900
 * are treated as missing (sentinel values from upstream systems).
 */
export const formatDateToNumericUTC = (
	date: Date | string | null | undefined,
): string => {
	if (date == null || date === "") return "";
	const d = date instanceof Date ? date : new Date(date);
	if (Number.isNaN(d.getTime())) return "Invalid Date";
	if (d.getUTCFullYear() <= 1900) return "";
	return NUMERIC_UTC_FORMATTER.format(d);
};

/**
 * Grid cell renderer that formats the bound field's value as a numeric UTC
 * date. Use with a column whose `field` points at a `Date` (preferred, to
 * enable correct sorting and `filter: "date"`) or an ISO date string.
 */
export const DateCell = (props: GridCustomCellProps) => {
	const dataItem = props.dataItem as Record<string, unknown>;
	const value = dataItem[props.field ?? ""] as Date | string | null | undefined;
	return (
		<GridTd tdProps={props.tdProps}>{formatDateToNumericUTC(value)}</GridTd>
	);
};
